import { ArrowUpRightIcon, CaretRightIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { SeverityBadge } from '../../components/badges/severity-badge.tsx'
import * as buttonStyles from '../../components/buttons/default-button.css.ts'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import { useToastService } from '../../services/toast-service.tsx'
import {
  invalidateGetScans,
  useGetAiFindingDetection,
  useGetRepositoryAiScanGeneration,
  useGetScanFindings,
  useGetScans,
  usePostAiScan,
  usePostAnalyzeScan,
} from '../../utils/api-client/api-hooks.ts'
import type { AiScanMode, AiScanStatus, FindingResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { getFirstCompletedRepresentativeTriageResult, isAnalysisInProgress } from '../../utils/finding-utils.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './ai-detection-tab.css.ts'
import { AiScanProgressCanvas } from './ai-scan-progress-canvas.tsx'
import { useRepositoryDetailsPageParams } from './repository-details-page.tsx'

const TabHeader: React.FC = () => (
  <div className={styles.header}>
    <h2 className={styles.headerTitle}>Pixee AI Scanner</h2>
    <p className={styles.headerDescription}>
      Generate security findings for this repository with Pixee's AI scanner — no upload required. Triage a finding to
      run Pixee's analysis on it; results open on the standard finding page, like any other tool.
    </p>
  </div>
)

export const AiDetectionTab: React.FC = () => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const queryClient = useQueryClient()

  // Every generate run is a batch; list the most recent Pixee-AI scans newest-first and stack them.
  const { data: scansData, isLoading: isScansLoading } = useGetScans({
    page: { number: 0, size: 20 },
    repositoryIds: [repositoryId],
    tools: ['pixee_ai'],
  })
  const batches = scansData?._embedded?.items ?? []

  // Whether a generation is in flight is read from the server, keyed by repository, so the progress
  // UI survives a reload and can't stick if an update is missed. Keyed by repository and not by scan
  // id on purpose: the analysis-service scan id exists only in the response to the POST that starts
  // the run, and a run takes minutes, so a reload loses it. It cannot be recovered from the batches
  // either — a platform scan is created only once the run finishes, so while it is generating there
  // is no scan row for it, and the newest batch is a *different*, already-finished run.
  const { data: generation } = useGetRepositoryAiScanGeneration({ repositoryId })
  const isGenerating = generation?.status === 'processing'
  const isFailed = generation?.status === 'failed'

  // Fire only on the processing → terminal transition, so an already-completed scan on first load
  // doesn't trigger a spurious refetch. Covers both the scan we started and one resumed after refresh.
  const previousStatusRef = useRef<AiScanStatus | undefined>(undefined)
  useEffect(() => {
    const previousStatus = previousStatusRef.current
    previousStatusRef.current = generation?.status
    if (previousStatus === 'processing' && generation && generation.status !== 'processing') {
      invalidateGetScans(queryClient)
    }
  }, [generation, queryClient])

  const showGenerateError = useShowMutationError<typeof usePostAiScan>({ prefix: 'Failed to start Pixee AI scan' })
  const generateMutation = usePostAiScan({
    // The generation record is written when the scan is dispatched, so refetching it here shows the
    // progress hero immediately rather than on the next poll.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repository-ai-scan-generation', repositoryId] })
      queryClient.invalidateQueries({ queryKey: ['scans'] })
    },
    onError: showGenerateError,
  })
  const isStarting = generateMutation.isPending
  const onGenerate = useCallback(
    (mode: AiScanMode) => generateMutation.mutate({ repositoryId, body: { mode } }),
    [generateMutation.mutate, repositoryId]
  )

  if (isScansLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading Pixee AI Scanner" />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <TabHeader />
      <GenerateActions
        onGenerate={onGenerate}
        isStarting={isStarting}
        isGenerating={isGenerating}
        hasBatches={batches.length > 0}
      />
      {isGenerating && <AiScanProgressCanvas findingsCount={generation?.findings_count ?? 0} />}
      {isFailed && (
        <FailedBanner
          failureReason={generation?.message}
          onRetry={() => onGenerate('generate')}
          isRetrying={isStarting}
        />
      )}
      {batches.length === 0 && !isGenerating && !isFailed ? (
        <EmptyState />
      ) : (
        batches.map(scan => <AiScanBatch key={scan.id} scanId={scan.id} importedAt={scan.imported_at} />)
      )}
    </div>
  )
}

// #region Actions & states

const GenerateActions: React.FC<{
  onGenerate: (mode: AiScanMode) => unknown
  isStarting: boolean
  isGenerating: boolean
  hasBatches: boolean
}> = ({ onGenerate, isStarting, isGenerating, hasBatches }) => {
  const primaryState = match({ isStarting, isGenerating })
    .with({ isStarting: true }, () => 'loading' as const)
    .with({ isGenerating: true }, () => 'disabled' as const)
    .otherwise(() => 'default' as const)
  return (
    <div className={styles.actions}>
      <DefaultButton buttonType="primary" state={primaryState} onClick={() => onGenerate('generate')}>
        {hasBatches ? 'Generate more findings' : 'Generate findings'}
      </DefaultButton>
      {/* `isGenerating` only turns true once the POST resolves, so `isStarting` is what keeps this
          from firing a second scan — in the other mode — while the first request is in flight. */}
      <DefaultButton
        buttonType="secondary"
        state={isStarting || isGenerating ? 'disabled' : 'default'}
        onClick={() => onGenerate('generate_triage_fix')}
      >
        Generate &amp; triage
      </DefaultButton>
    </div>
  )
}

const EmptyState: React.FC = () => (
  <div className={styles.emptyState}>
    <p className={styles.emptyStateTitle}>No Pixee AI findings yet</p>
    <p className={styles.emptyStateDescription}>
      Generate findings to detect issues, or generate &amp; triage to detect and analyze them in one pass. Generation
      usually takes a couple of minutes.
    </p>
  </div>
)

const FailedBanner: React.FC<{ failureReason?: string; onRetry: () => unknown; isRetrying: boolean }> = ({
  failureReason,
  onRetry,
  isRetrying,
}) => (
  <div role="alert" className={styles.failedBanner}>
    <p className={styles.failedBannerTitle}>
      <WarningCircleIcon size={20} weight="fill" />
      Scan failed
    </p>
    <p className={styles.failedBannerReason}>{failureReason ?? 'Something went wrong while generating findings.'}</p>
    <DefaultButton buttonType="secondary" size="small" state={isRetrying ? 'loading' : 'default'} onClick={onRetry}>
      Try again
    </DefaultButton>
  </div>
)

// #endregion

// #region Batches & findings

const AiScanBatch: React.FC<{ scanId: string; importedAt: string }> = ({ scanId, importedAt }) => {
  const { data, isLoading } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 100 },
    embed: ['items.representative-results.latest-patch'],
    // Poll while any finding is still being triaged so the button flips to "View triage" on its own.
    refetchInterval: query => (query.state.data?._embedded?.items?.some(isAnalysisInProgress) ? 1000 : false),
  })

  return (
    <AiScanBatchPure
      scanId={scanId}
      importedAt={importedAt}
      findings={data?._embedded?.items ?? []}
      isLoading={isLoading}
    />
  )
}

const AiScanBatchPure: React.FC<{
  scanId: string
  importedAt: string
  findings: FindingResponse[]
  isLoading: boolean
}> = ({ scanId, importedAt, findings, isLoading }) => (
  <div className={styles.batch}>
    <div className={styles.batchHeader}>
      <span className={styles.batchTimestamp}>
        Generated {formatDate({ date: importedAt, type: 'mm/dd/yyyy-hh:mm' })}
      </span>
      {!isLoading && (
        <span className={styles.batchCount}>
          · {findings.length} {pluralize(findings.length, 'finding', 'findings')}
        </span>
      )}
    </div>
    {isLoading ? (
      <div className={styles.batchLoading}>
        <Spinner size="16" label="Loading findings" />
      </div>
    ) : findings.length === 0 ? (
      <p className={styles.batchEmpty}>No findings were generated in this scan.</p>
    ) : (
      <div className={styles.findingList}>
        {findings.map(finding => (
          <AiFindingCard key={finding.id} scanId={scanId} finding={finding} />
        ))}
      </div>
    )}
  </div>
)

const AiFindingCard: React.FC<{ scanId: string; finding: FindingResponse }> = ({ scanId, finding }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className={styles.findingCard}>
      <div className={styles.findingSummary}>
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(open => !open)}
          className={styles.findingDisclosure}
        >
          <CaretRightIcon size={13} color={mappedColors.neutral.neutral30} className={styles.findingDisclosureIcon} />
          <span className={styles.findingTitle}>{finding.title ?? finding.rule}</span>
        </button>
        <span className={styles.findingRule}>{finding.rule}</span>
        <SeverityBadge severity={finding.severity} />
        <TriageAction scanId={scanId} finding={finding} />
      </div>
      {isOpen && <AiFindingDetail scanId={scanId} finding={finding} />}
    </div>
  )
}

// Inline detection detail — everything derivable from the SARIF, shown before triage (ISS-8867).
// Triage results are not shown here; they open on the standard finding page via "View triage".
const AiFindingDetail: React.FC<{ scanId: string; finding: FindingResponse }> = ({ scanId, finding }) => {
  const { data: detail, isLoading } = useGetAiFindingDetection({ scanId, findingId: finding.id })

  if (isLoading) {
    return (
      <div className={styles.detailLoading}>
        <Spinner size="16" label="Loading detection detail" />
      </div>
    )
  }

  const hasDetail =
    !!detail && (!!detail.description || !!detail.location || !!detail.matched_sink || detail.confidence !== undefined)

  return (
    <div className={styles.detail}>
      <div className={styles.detailFacts}>
        <span>
          CWE <b className={styles.detailFactCode}>{finding.rule}</b>
        </span>
        {detail?.matched_sink && (
          <span>
            Matched sink <b className={styles.detailFactCode}>{detail.matched_sink}</b>
          </span>
        )}
        {detail?.confidence !== undefined && (
          <span>
            Confidence <b className={styles.detailFactValue}>{Math.round(detail.confidence * 100)}%</b>
          </span>
        )}
      </div>

      {detail?.description && (
        <DetailSection label="Description">
          <p className={styles.detailDescription}>{detail.description}</p>
        </DetailSection>
      )}

      {detail?.location && (
        <DetailSection label="Location">
          {detail.location.snippet && (
            <CodeSnippet
              snippet={detail.location.snippet}
              startLine={detail.location.snippet_start_line ?? detail.location.line}
              highlightLine={detail.location.line}
            />
          )}
          <span className={styles.detailFilePath}>{`${detail.location.file}:${detail.location.line}`}</span>
        </DetailSection>
      )}

      {!hasDetail && (
        <p className={styles.detailUnavailable}>
          Detection detail (code location, snippet, matched sink, confidence) isn't available for this finding yet.
          Triage it to run Pixee's analysis.
        </p>
      )}
    </div>
  )
}

const DetailSection: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className={styles.detailSection}>
    <p className={styles.detailSectionLabel}>{label}</p>
    {children}
  </div>
)

// Line-numbered code block that highlights the vulnerable line, so the offending code reads at a glance.
const CodeSnippet: React.FC<{ snippet: string; startLine: number; highlightLine: number }> = ({
  snippet,
  startLine,
  highlightLine,
}) => (
  <pre className={styles.snippet}>
    {snippet.split('\n').map((text, index) => {
      const lineNumber = startLine + index
      const isHighlighted = lineNumber === highlightLine
      return (
        <div
          key={lineNumber}
          className={classNames(styles.snippetLine, isHighlighted && styles.snippetLineHighlighted)}
        >
          <span className={styles.snippetLineNumber}>{lineNumber}</span>
          <span className={classNames(styles.snippetText, isHighlighted && styles.snippetTextHighlighted)}>
            {text || ' '}
          </span>
        </div>
      )
    })}
  </pre>
)

const TriageAction: React.FC<{ scanId: string; finding: FindingResponse }> = ({ scanId, finding }) => {
  const { addToast } = useToastService()
  const showTriageError = useShowMutationError<typeof usePostAnalyzeScan>({ prefix: 'Unable to start triage' })
  const analyzeMutation = usePostAnalyzeScan({
    onSuccess: () => addToast({ message: <>Triage started</>, variant: 'success' }),
    onError: showTriageError,
  })

  const completedTriage = getFirstCompletedRepresentativeTriageResult(finding)
  const inProgress = isAnalysisInProgress(finding) || analyzeMutation.isPending

  if (inProgress) {
    return (
      <DefaultButton buttonType="secondary" size="small" state="loading">
        Triaging
      </DefaultButton>
    )
  }

  if (completedTriage) {
    return (
      <Link
        to={Routes.ScanDetailsPage.createPath({ scanId, triageFindingId: finding.id })}
        className={classNames(
          buttonStyles.button({ buttonType: 'secondary', state: 'default', size: 'small' }),
          styles.triageLink
        )}
      >
        View triage
        <ArrowUpRightIcon size={13} weight="bold" />
      </Link>
    )
  }

  return (
    <DefaultButton
      buttonType="secondary"
      size="small"
      onClick={() => analyzeMutation.mutate({ scanId, findingIds: [finding.id] })}
    >
      Triage
    </DefaultButton>
  )
}

// #endregion
